import { useState } from "react";

import SortableTable, { SortableTableColumn } from "components/SortableTable";
import { EmptystateCodeFileColored } from "components/icons/generated";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import { ConfigType, SearchQueryOrderDirection } from "types/generated";
import Tooltip from "ds/components/Tooltip";
import PageInfoHeader from "ds/components/PageInfoHeader";

import { ContextContext } from "../Context";
import { COLUMN_ORDER_MOUNTED_FILES_HEADER } from "../constants";
import { showContextMountedFilesFormDrawer } from "./FormDrawer";
import ContextMountedFilesListItem from "./ListItem";
import { showContextMountedFilesUpdateFormDrawer } from "./UpdateFormDrawer";
import { ContextMountedFile } from "./types";

const columns: SortableTableColumn[] = [
  {
    value: "id",
    label: "Path",
    colStart: "span 2",
  },
  {
    value: "writeOnly",
    label: "Secret",
  },
  {
    value: "createdAt",
    label: "Created at",
  },
];

const ContextMountedFiles = () => {
  const { context, canUpdateContext, isContextLoading } = useTypedContext(ContextContext);
  const [fileDraft, setFileDraft] = useState<ContextMountedFile | undefined>(undefined);

  const { config } = context;

  useTitle(`Mounted files · ${context.name}`);

  const handleOpenDraftDetailsDrawer = () => {
    showContextMountedFilesFormDrawer({ item: fileDraft, handleFileAdd });
  };

  const handleOpenFileDetailsDrawer = (item: ContextMountedFile) => {
    showContextMountedFilesUpdateFormDrawer({ item, contextId: context.id });
  };

  const handleFileAdd = (file: ContextMountedFile) => {
    setFileDraft(file);
  };

  const handleRemoveFileDraft = () => {
    setFileDraft(undefined);
  };

  const items = config.filter((item) => item.type === ConfigType.FileMount);

  return (
    <>
      <PageInfoHeader title="Mounted files">
        <Tooltip
          active={!!canUpdateContext.message}
          on={(props) => (
            <Button
              {...props}
              variant="primary"
              onPress={handleOpenDraftDetailsDrawer}
              disabled={!!fileDraft || !canUpdateContext.result}
            >
              Add file
            </Button>
          )}
        >
          {canUpdateContext.message}
        </Tooltip>
      </PageInfoHeader>
      {/* TODO: Add loading state */}
      {(items.length !== 0 || fileDraft || isContextLoading) && (
        <SortableTable
          ariaLabel="Mounted files"
          items={items}
          columns={columns}
          columnOrder={COLUMN_ORDER_MOUNTED_FILES_HEADER}
          renderItems={(sorted) =>
            (sorted.length || fileDraft) && (
              <Box direction="column" padding="large" gap="large">
                {fileDraft && (
                  <ContextMountedFilesListItem
                    item={fileDraft}
                    onRemoveFileDraft={handleRemoveFileDraft}
                    onOpenDetailsDrawer={handleOpenDraftDetailsDrawer}
                  />
                )}

                {sorted.map((item) => (
                  <ContextMountedFilesListItem
                    key={item.id}
                    item={item}
                    onOpenFileDetailsDrawer={handleOpenFileDetailsDrawer}
                  />
                ))}
              </Box>
            )
          }
          initialSortBy="createdAt"
          initialDirection={SearchQueryOrderDirection.Desc}
        />
      )}

      {items.length === 0 && !fileDraft && !isContextLoading && (
        <EmptyState
          padding="large 0"
          icon={EmptystateCodeFileColored}
          title="You do not have any mounted files yet"
          caption="If you need to access a file in multiple configurations, you can upload it here."
        />
      )}
    </>
  );
};

export default ContextMountedFiles;
